/**
* catalog-flipbook.js
* XStamper — PDF catalog flipbook (Catalog page)
*
* Click the cover -> full-screen viewer -> lazy-load pdf.js + page-flip
* -> render PDF pages to images -> page-flip animates the book.
* Viewer chrome: chevron arrows, page counter, draggable page slider,
* print / download / share / fullscreen buttons.
*
* Loading is progressive: the book opens as soon as the first few pages are
* ready; the remaining pages render in the background and are swapped in via
* pageFlip.updateFromHtml (which preserves the reader's current page).
*
* Vanilla JS, no dependencies. pdf.js and page-flip are only fetched on first
* click so the Catalog page stays light until a visitor actually opens the book.
*/

(function () {
  'use strict';

  // Open on the cover alone: with a correctly linearized PDF its data sits in
  // the first ~1.5MB, so the book appears in a few seconds even on slow
  // connections while the background render fills in the rest (the bar shows
  // "Loading N / total" until every page is in).
  var INITIAL_PAGES = 1;
  // Rasterize pages to match the display's pixel density: a fixed low scale
  // looks blurry on scaled/HiDPI screens (Windows 125-150% zoom, Retina).
  // The floor of 2 keeps narrow desktop windows crisp. Capped at 2.5 (still
  // ~1530x1980 px per page, ample pinch-zoom headroom): canvas memory scales
  // with the SQUARE of the scale, and the highest-dpr devices (phones) are
  // exactly the ones with the least memory.
  var RENDER_SCALE = Math.min(2.5, Math.max(2, 1.5 * (window.devicePixelRatio || 1)));
  var JPEG_QUALITY = 0.85;
  var SLIDER_FILL = '#3d8bff';
  var SLIDER_TRACK = 'rgba(255, 255, 255, 0.25)';

  var scriptLoadPromises = {};

  function loadScript(src) {
    if (scriptLoadPromises[src]) return scriptLoadPromises[src];
    scriptLoadPromises[src] = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = function () {
        delete scriptLoadPromises[src];
        reject(new Error('Failed to load ' + src));
      };
      document.head.appendChild(script);
    });
    return scriptLoadPromises[src];
  }

  var pdfjsLoadPromise = null;

  function loadPdfjs(pdfjsSrc, workerSrc) {
    if (pdfjsLoadPromise) return pdfjsLoadPromise;
    pdfjsLoadPromise = new Promise(function (resolve, reject) {
      if (window.__xsPdfjsLib) {
        resolve(window.__xsPdfjsLib);
        return;
      }
      // pdf.js is an ES module, but a dynamic import() written here would be
      // rewritten to require() by Shopify's CDN asset minifier and crash in
      // the browser. Keeping the import inside a string, executed as a real
      // inline module script, makes it invisible to the minifier.
      var script = document.createElement('script');
      script.type = 'module';
      script.textContent =
        'import * as pdfjsLib from ' + JSON.stringify(pdfjsSrc) + ';' +
        'pdfjsLib.GlobalWorkerOptions.workerSrc = ' + JSON.stringify(workerSrc) + ';' +
        'window.__xsPdfjsLib = pdfjsLib;' +
        'window.dispatchEvent(new Event("xs:pdfjs-ready"));';

      var timer = setTimeout(function () {
        cleanup();
        reject(new Error('Timed out loading pdf.js'));
      }, 30000);
      function onReady() {
        cleanup();
        resolve(window.__xsPdfjsLib);
      }
      function onError() {
        cleanup();
        reject(new Error('Failed to load pdf.js module from ' + pdfjsSrc));
      }
      function cleanup() {
        clearTimeout(timer);
        window.removeEventListener('xs:pdfjs-ready', onReady);
        script.removeEventListener('error', onError);
      }
      window.addEventListener('xs:pdfjs-ready', onReady);
      script.addEventListener('error', onError);
      document.head.appendChild(script);
    }).catch(function (err) {
      pdfjsLoadPromise = null;
      throw err;
    });
    return pdfjsLoadPromise;
  }

  function renderPage(pdf, num) {
    return pdf.getPage(num).then(function (page) {
      var viewport = page.getViewport({ scale: RENDER_SCALE });
      var canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      return page
        .render({ canvasContext: canvas.getContext('2d'), viewport: viewport })
        .promise.then(function () {
          var w = viewport.width;
          var h = viewport.height;
          return new Promise(function (resolve) {
            // toDataURL produces a base64 string ~37% larger than the binary
            // and pins it in the JS heap for the life of the page. A Blob URL
            // keeps the bytes out of the heap and can be revoked later if we
            // ever add page eviction.
            canvas.toBlob(function (blob) {
              var src = blob
                ? URL.createObjectURL(blob)
                : canvas.toDataURL('image/jpeg', JPEG_QUALITY); // toBlob can yield null
              // Release the multi-MB backing store NOW rather than waiting for
              // GC. With 136 pages this is the difference between working and
              // running out of memory on phones.
              canvas.width = canvas.height = 0;
              resolve({ src: src, width: w, height: h });
            }, 'image/jpeg', JPEG_QUALITY);
          });
        });
    });
  }

  // Render pages with LIMITED concurrency. Fully parallel keeps every canvas
  // alive at once (several MB each - unstable on phones); fully sequential
  // serializes the network round-trips of on-demand range fetches and roughly
  // doubles time-to-open on slow connections (measured). Three in flight
  // bounds memory to ~3 canvases while keeping network and decode overlapped.
  var RENDER_POOL = 3;

  function renderPooled(pdf, nums, onEach) {
    var out = new Array(nums.length);
    var next = 0;
    var completed = 0;

    function worker() {
      if (next >= nums.length) return Promise.resolve();
      var idx = next++;
      return renderPage(pdf, nums[idx]).then(function (img) {
        out[idx] = img;
        completed++;
        if (onEach) onEach(completed);
        return worker();
      });
    }

    var lanes = [];
    for (var i = 0; i < Math.min(RENDER_POOL, nums.length); i++) lanes.push(worker());
    return Promise.all(lanes).then(function () {
      return out;
    });
  }

  // Pages are shown as real <img> elements (page-flip HTML mode): the browser
  // scales those density-aware, unlike the library's canvas mode, which sizes
  // its canvas in CSS pixels and looks soft on HiDPI/scaled displays.
  function makePageElement(src, lazy) {
    var el = document.createElement('div');
    el.className = 'catalog-flipbook__page';
    var img = document.createElement('img');
    if (lazy) img.loading = 'lazy';
    img.src = src;
    img.alt = '';
    img.draggable = false;
    el.appendChild(img);
    return el;
  }

  function initCatalog(root) {
    var pdfUrl = root.getAttribute('data-pdf-url');
    var trigger = root.querySelector('[data-catalog-open]');
    var overlay = root.querySelector('[data-catalog-overlay]');
    if (!pdfUrl || !trigger || !overlay) return;

    var pageFlipSrc = root.getAttribute('data-pageflip-src');
    var pdfjsSrc = root.getAttribute('data-pdfjs-src');
    var pdfWorkerSrc = root.getAttribute('data-pdfjs-worker-src');
    var wasmPrefix = root.getAttribute('data-pdfjs-wasm-prefix');
    // Pre-rendered images mode: when the merchant provides a base URL of
    // page JPEGs (page-001.jpg, page-002.jpg, ...) plus the page count, the
    // viewer skips pdf.js entirely and the book opens after ONE small image
    // instead of processing the whole PDF client-side.
    var imagesBase = root.getAttribute('data-images-base');
    var imagesCount = parseInt(root.getAttribute('data-images-count') || '0', 10);

    var closeButtons = overlay.querySelectorAll('[data-catalog-close]');
    var flipContainer = overlay.querySelector('[data-catalog-flip]');
    var prevBtn = overlay.querySelector('[data-catalog-prev]');
    var nextBtn = overlay.querySelector('[data-catalog-next]');
    var loadingEl = overlay.querySelector('[data-catalog-loading]');
    var loadingTextEl = overlay.querySelector('[data-catalog-loading-text]');
    var errorEl = overlay.querySelector('[data-catalog-error]');
    var barEl = overlay.querySelector('[data-catalog-bar]');
    var pageLabelEl = overlay.querySelector('[data-catalog-page-label]');
    var loadHintEl = overlay.querySelector('[data-catalog-load-hint]');
    var sliderEl = overlay.querySelector('[data-catalog-slider]');
    var printBtn = overlay.querySelector('[data-catalog-print]');
    var downloadBtn = overlay.querySelector('[data-catalog-download]');
    var shareBtn = overlay.querySelector('[data-catalog-share]');
    var shareToastEl = overlay.querySelector('[data-catalog-share-toast]');
    var fullscreenBtn = overlay.querySelector('[data-catalog-fullscreen]');

    var pageFlip = null;
    var loadStarted = false;
    var totalPages = 0;
    var loadedPages = 0;

    /* ── Page label + slider ──────────────────────────────────────────── */

    // Spreads sit on odd indexes (cover alone at 0, then [1,2], [3,4], ...):
    // snap any index to its spread's left page so label and book agree.
    function snapToSpread(index) {
      if (index > 0 && index % 2 === 0) return index - 1;
      return index;
    }

    function spreadLabel(index) {
      index = snapToSpread(index);
      var left = index + 1;
      var right = Math.min(index + 2, totalPages);
      if (index === 0 || left >= right) return left + ' / ' + totalPages;
      return left + ' – ' + right + ' / ' + totalPages;
    }

    function paintSliderFill() {
      var max = parseInt(sliderEl.max, 10) || 1;
      var pct = (parseInt(sliderEl.value, 10) / max) * 100;
      sliderEl.style.background =
        'linear-gradient(to right, ' + SLIDER_FILL + ' 0%, ' + SLIDER_FILL + ' ' + pct + '%, ' + SLIDER_TRACK + ' ' + pct + '%)';
    }

    function syncBar(index) {
      pageLabelEl.textContent = spreadLabel(index);
      sliderEl.value = index;
      paintSliderFill();
    }

    /* ── Loading pipeline ─────────────────────────────────────────────── */

    // Swap freshly rendered pages into the book every CHUNK pages (not just
    // once at the end) so a reader flipping forward never hits a false "end
    // of book" while the rest of the catalog is still rendering.
    var UPDATE_CHUNK = 6;

    function renderRemainingPages(pdf, pageEls, from, total) {
      var sinceUpdate = 0;

      function applyUpdate(isFinal) {
        if (!pageFlip) return;
        pageFlip.updateFromHtml(pageEls);
        sliderEl.max = loadedPages - 1;
        syncBar(pageFlip.getCurrentPageIndex());
        if (loadHintEl) {
          if (isFinal) {
            loadHintEl.hidden = true;
          } else {
            loadHintEl.hidden = false;
            loadHintEl.textContent = 'Loading ' + loadedPages + ' / ' + total + '…';
          }
        }
      }

      var chain = Promise.resolve();
      var makeStep = function (num) {
        return function () {
          return renderPage(pdf, num).then(function (img) {
            pageEls.push(makePageElement(img.src));
            loadedPages = num;
            sinceUpdate++;
            if (sinceUpdate >= UPDATE_CHUNK && num < total) {
              sinceUpdate = 0;
              applyUpdate(false);
            }
          });
        };
      };
      for (var num = from; num <= total; num++) {
        chain = chain.then(makeStep(num));
      }
      return chain.then(function () {
        applyUpdate(true);
      });
    }

    function initBook(width, height, pageEls) {
      loadingEl.hidden = true;
      pageFlip = new window.St.PageFlip(flipContainer, {
        width: width,
        height: height,
        size: 'stretch',
        minWidth: 280,
        maxWidth: 1400,
        minHeight: 360,
        maxHeight: 1800,
        maxShadowOpacity: 0.5,
        showCover: true,
        mobileScrollSupport: false
      });
      pageFlip.loadFromHTML(pageEls);
      pageFlip.on('flip', function (e) {
        syncBar(e.data);
      });
      barEl.hidden = false;
      sliderEl.max = Math.max(pageEls.length - 1, 1);
      syncBar(0);
    }

    function pageImageUrl(num) {
      var n = String(num);
      while (n.length < 3) n = '0' + n;
      return imagesBase.replace(/\/+$/, '') + '/page-' + n + '.jpg';
    }

    function startImagesMode() {
      totalPages = imagesCount;
      var probe = new Image();
      probe.src = pageImageUrl(1);
      var decoded = probe.decode ? probe.decode() : Promise.resolve();
      Promise.all([loadScript(pageFlipSrc), decoded])
        .then(function () {
          var pageEls = [];
          for (var n = 1; n <= imagesCount; n++) {
            // First few pages load eagerly; the rest lazy-load as the reader
            // approaches them (page-flip keeps far pages out of the viewport).
            pageEls.push(makePageElement(pageImageUrl(n), n > 6));
          }
          initBook(probe.naturalWidth || 1000, probe.naturalHeight || 1294, pageEls);
          loadedPages = imagesCount;
        })
        .catch(function (err) {
          loadStarted = false;
          loadingEl.hidden = true;
          errorEl.hidden = false;
          window.console && console.error('Catalog flipbook failed to load page images:', err);
        });
    }

    function startLoad() {
      loadStarted = true;
      loadingEl.hidden = false;
      errorEl.hidden = true;

      if (imagesBase && imagesCount > 0) {
        startImagesMode();
        return;
      }

      Promise.all([loadScript(pageFlipSrc), loadPdfjs(pdfjsSrc, pdfWorkerSrc)])
        .then(function (results) {
          var pdfjsLib = results[1];
          // pdf.js v5+ removed the string-argument shorthand: getDocument must
          // receive an options object with a `url` key.
          // On-demand ranges, tuned by measurement against the live Space and
          // a CORRECTLY linearized PDF (qpdf --check-linearization must be
          // clean - a broken hint table forces a ~full download before page 1
          // in every mode). Measured on the repaired 128MB catalog: opens
          // after ~6MB. disableStream avoids a competing full-file stream;
          // disableAutoFetch means early leavers transfer ~6MB, not 128MB;
          // 512KB chunks beat both 64KB (round-trip latency x ~70 chunks)
          // and 1-2MB (over-fetch per touched object) in live tests.
          var params = {
            url: pdfUrl,
            disableStream: true,
            disableAutoFetch: true,
            rangeChunkSize: 524288
          };
          // wasmUrl must end in "/" (pdf.js validates); it appends fixed
          // filenames (jbig2.wasm, openjpeg.wasm, ...), so those live flat
          // in assets/ under their exact upstream names.
          if (wasmPrefix) params.wasmUrl = wasmPrefix;
          return pdfjsLib.getDocument(params).promise;
        })
        .then(function (pdf) {
          var total = pdf.numPages;
          if (!total) throw new Error('Catalog PDF has no pages');
          totalPages = total;
          var initialCount = Math.min(INITIAL_PAGES, total);

          var initialNums = [];
          for (var i = 1; i <= initialCount; i++) initialNums.push(i);

          return renderPooled(pdf, initialNums, function (n) {
            loadingTextEl.textContent = 'Loading catalog… ' + n + '/' + initialCount;
          }).then(function (initialImages) {
            var first = initialImages[0];
            var pageEls = initialImages.map(function (img) {
              return makePageElement(img.src);
            });
            initBook(first.width, first.height, pageEls);
            loadedPages = initialCount;

            if (total > initialCount && loadHintEl) {
              loadHintEl.hidden = false;
              loadHintEl.textContent = 'Loading ' + initialCount + ' / ' + total + '…';
            }

            if (total > initialCount) {
              renderRemainingPages(pdf, pageEls, initialCount + 1, total).catch(function (err) {
                window.console && console.error('Catalog flipbook: background page render failed:', err);
              });
            } else {
              sliderEl.max = total - 1;
            }
          });
        })
        .catch(function (err) {
          loadStarted = false;
          loadingEl.hidden = true;
          errorEl.hidden = false;
          window.console && console.error('Catalog flipbook failed to load:', err);
        });
    }

    /* ── Open / close ─────────────────────────────────────────────────── */

    function openOverlay(e) {
      if (e) e.preventDefault();
      var sbw = window.innerWidth - document.documentElement.clientWidth;
      overlay.hidden = false;
      overlay.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      if (sbw > 0) document.body.style.paddingRight = sbw + 'px';
      requestAnimationFrame(function () {
        overlay.classList.add('is-open');
      });
      if (!loadStarted) startLoad();
    }

    function closeOverlay(e) {
      if (e) e.preventDefault();
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(function () {});
      }
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      document.body.style.paddingRight = '';
      trigger.focus();
    }

    trigger.addEventListener('click', openOverlay);
    closeButtons.forEach(function (btn) {
      btn.addEventListener('click', closeOverlay);
    });

    /* ── Navigation ───────────────────────────────────────────────────── */

    prevBtn.addEventListener('click', function () {
      if (pageFlip) pageFlip.flipPrev();
    });
    nextBtn.addEventListener('click', function () {
      if (pageFlip) pageFlip.flipNext();
    });

    document.addEventListener('keydown', function (e) {
      if (!overlay.classList.contains('is-open')) return;
      if (e.key === 'Escape') {
        closeOverlay();
        return;
      }
      if (e.target === sliderEl) return;
      if (e.key === 'ArrowLeft' && pageFlip) pageFlip.flipPrev();
      if (e.key === 'ArrowRight' && pageFlip) pageFlip.flipNext();
    });

    // Scrubbing turns pages LIVE while dragging (rAF-throttled); the change
    // event commits the final position on release.
    var scrubQueued = false;

    function scrubTarget() {
      return snapToSpread(Math.min(parseInt(sliderEl.value, 10), loadedPages - 1));
    }

    sliderEl.addEventListener('input', function () {
      pageLabelEl.textContent = spreadLabel(parseInt(sliderEl.value, 10));
      paintSliderFill();
      if (!pageFlip || scrubQueued) return;
      scrubQueued = true;
      requestAnimationFrame(function () {
        scrubQueued = false;
        if (pageFlip) pageFlip.turnToPage(scrubTarget());
      });
    });
    sliderEl.addEventListener('change', function () {
      if (!pageFlip) return;
      var v = scrubTarget();
      pageFlip.turnToPage(v);
      syncBar(v);
    });

    /* ── Toolbar actions ──────────────────────────────────────────────── */

    if (printBtn) {
      printBtn.addEventListener('click', function () {
        window.open(pdfUrl, '_blank', 'noopener');
      });
    }

    if (downloadBtn) {
      downloadBtn.addEventListener('click', function () {
        // Previously this did fetch(pdfUrl) -> res.blob(), buffering the whole
        // multi-MB file in memory just to set a download filename that the URL
        // already carries. Open it and let the browser handle saving.
        window.open(pdfUrl, '_blank', 'noopener');
      });
    }

    if (shareBtn) {
      shareBtn.addEventListener('click', function () {
        var shareData = { title: document.title, url: window.location.href };
        if (navigator.share) {
          navigator.share(shareData).catch(function () {});
        } else if (navigator.clipboard) {
          navigator.clipboard.writeText(shareData.url).then(function () {
            if (!shareToastEl) return;
            shareToastEl.hidden = false;
            setTimeout(function () {
              shareToastEl.hidden = true;
            }, 1600);
          });
        }
      });
    }

    if (fullscreenBtn) {
      if (!overlay.requestFullscreen) {
        fullscreenBtn.hidden = true;
      } else {
        fullscreenBtn.addEventListener('click', function () {
          if (document.fullscreenElement) {
            document.exitFullscreen().catch(function () {});
          } else {
            overlay.requestFullscreen().catch(function () {});
          }
        });
      }
    }
  }

  document.querySelectorAll('[data-catalog-flipbook]').forEach(initCatalog);
})();
